/**
 * The confirmation — and a link into the calendar it just filled.
 *
 * Holds no DTO and no arithmetic: `model/assignConfirmation.ts` decides what the summary says and
 * where the link goes, which is also the only place a test can reach either. The link is `null`
 * when the response did not carry both ids, and then nothing pressable is drawn: a control that
 * looks like a link and leads nowhere is worse than one that is not there.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { AssignConfirmation } from '../model/assignConfirmation';

import { TickGlyph } from './AssignGlyphs';
import styles from './AssignPlanDialog.module.css';

interface AssignDoneProps {
  readonly confirmation: AssignConfirmation;
  /**
   * `null` where there is no second client to assign to — see `AssignFlow.assignAgain`.
   *
   * Absent rather than disabled: a disabled control that carries an explanation stays focusable and
   * says why, and there is nothing to say here beyond "this window is about one person". The
   * confirmation's own link into their calendar is what the trainer wants next.
   */
  readonly onAssignAgain: (() => void) | null;
}

export function AssignDone({ confirmation, onAssignAgain }: AssignDoneProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.done}>
      <span className={styles.doneMark} aria-hidden="true">
        <TickGlyph />
      </span>
      <h3 className={styles.doneTitle}>{confirmation.title}</h3>
      <p className={styles.doneBody}>{confirmation.body}</p>
      <p className={styles.doneSummary}>{confirmation.summary}</p>
      <div className={styles.doneLinks}>
        {confirmation.calendarLink !== null && (
          <Link {...confirmation.calendarLink} className={styles.doneLink}>
            {t('web:assign.done.openCalendar')}
          </Link>
        )}
        {onAssignAgain !== null && (
          <button type="button" className={styles.doneAgain} onClick={onAssignAgain}>
            {t('web:assign.done.assignAgain')}
          </button>
        )}
      </div>
    </div>
  );
}
